/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  CustomFieldPlacement,
  CustomFieldRestService,
  CustomFieldStatus,
  CustomFieldType,
} from '@gh/builder-shared-data';
import { DictionaryService, InitDialogViewService, InitViewService } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SlicedDataSource } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { omit } from 'lodash';
import { Observable } from 'rxjs';
import { CustomFieldDetails, CustomFieldListItem } from './custom-field.model';

export interface CustomFieldDetailsDialogConfig {
  id?: ID;
}

@Injectable()
export class CustomFieldListBusinessService implements InitViewService,
                                                       InitDialogViewService<CustomFieldDetailsDialogConfig> {

  constructor(private restService: CustomFieldRestService,
              private gridLayoutService: GridLayoutService,
              private dictionaryService: DictionaryService) {

  }

  initView(): any {
    return {
      gridLayout: this.gridLayoutService.get('customFields'),
    };
  }

  initDialogView(route: DialogRouteSnapshot<CustomFieldDetailsDialogConfig>): any {
    return {
      placements: this.dictionaryService.getByEnum(CustomFieldPlacement),
      statuses: this.dictionaryService.getByEnum(CustomFieldStatus),
      types: this.dictionaryService.getByEnum(CustomFieldType),
      customField: route.input.id ? this.get(<ID>route.input.id) : {},
    };
  }

  findAllAsDataSource(): SlicedDataSource<CustomFieldListItem> {
    return this.restService.findAllAsDataSource();
  }

  save(value: CustomFieldDetails): Observable<CustomFieldDetails> {
    const customField = <CustomFieldDetails>omit(value, 'textLength');
    return customField.customFieldId ? this.update(customField) : this.create(customField);
  }

  create(customField: CustomFieldDetails): Observable<CustomFieldDetails> {
    return this.restService.create(customField);
  }

  update(customField: CustomFieldDetails): Observable<CustomFieldDetails> {
    return this.restService.update(customField);
  }

  deactivate(customField: CustomFieldDetails): Observable<CustomFieldDetails> {
    return this.restService.deactivate(customField);
  }

  activate(customField: CustomFieldDetails): Observable<CustomFieldDetails> {
    return this.restService.activate(customField);
  }

  get(id: ID): Observable<CustomFieldDetails> {
    return this.restService.get(id);
  }
}
